import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';

interface SecurityVulnerability {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  cve?: string;
  package: string;
  version: string;
  fixedVersion?: string;
  discovered: string;
  status: 'open' | 'fixed' | 'ignored';
  category: string;
}

interface SecurityMetrics {
  totalVulnerabilities: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  fixed: number;
  open: number;
  lastScan: string;
  scanDuration: number;
}

const SecurityComponent: React.FC = () => {
  const [vulnerabilities, setVulnerabilities] = useState<SecurityVulnerability[]>([]);
  const [metrics, setMetrics] = useState<SecurityMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [isScanning, setIsScanning] = useState(false);

  useEffect(() => {
    fetchSecurityData();
  }, []);

  const fetchSecurityData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [vulnResponse, metricsResponse] = await Promise.all([
        apiService.getSecurityVulnerabilities(),
        apiService.getSecurityMetrics()
      ]);

      if (vulnResponse.success && vulnResponse.data) {
        setVulnerabilities(vulnResponse.data.vulnerabilities || []);
      }

      if (metricsResponse.success && metricsResponse.data) {
        setMetrics(metricsResponse.data);
      }

      if (!vulnResponse.success && !metricsResponse.success) {
        setError('Failed to fetch security data');
      }
    } catch (error) {
      console.error('Error fetching security data:', error);
      setError('Failed to fetch security data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const runSecurityScan = async () => {
    setIsScanning(true);
    setError(null);
    try {
      const response = await apiService.runSecurityScan();
      if (response.success) {
        await fetchSecurityData();
      } else {
        setError(response.message || 'Failed to run security scan');
      }
    } catch (error) {
      console.error('Error running security scan:', error);
      setError('Failed to run security scan. Please try again.');
    } finally {
      setIsScanning(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'fixed': return 'bg-scarf-green/20 text-scarf-green';
      case 'open': return 'bg-brand-red/20 text-brand-red';
      case 'ignored': return 'bg-gray-100 text-gray-500';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const filteredVulnerabilities = vulnerabilities.filter(vuln =>
    selectedSeverity === 'all' || vuln.severity === selectedSeverity
  );

  if (isLoading && !metrics) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-600 text-lg">Loading security data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-brand-red rounded-xl p-4">
          {error}
        </div>
      )}

      {/* Security Metrics */}
      {metrics && (
        <div className="grid md:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Total Vulnerabilities</h3>
            <div className="text-3xl font-bold text-brand-red">{metrics.totalVulnerabilities}</div>
            <p className="text-gray-500 text-sm">All time</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Critical</h3>
            <div className="text-3xl font-bold text-red-600">{metrics.critical}</div>
            <p className="text-gray-500 text-sm">High priority</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Fixed</h3>
            <div className="text-3xl font-bold text-scarf-green">{metrics.fixed}</div>
            <p className="text-gray-500 text-sm">Resolved</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Last Scan</h3>
            <div className="text-lg font-bold text-brand-blue-light">
              {new Date(metrics.lastScan).toLocaleDateString()}
            </div>
            <p className="text-gray-500 text-sm">{metrics.scanDuration}s duration</p>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={runSecurityScan}
            disabled={isScanning || isLoading}
            className="bg-brand-red hover:bg-brand-red/90 disabled:bg-brand-red/50 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            {isScanning ? 'Scanning...' : 'Run Security Scan'}
          </button>
          <button
            onClick={fetchSecurityData}
            disabled={isLoading}
            className="bg-brand-blue-light hover:bg-brand-blue disabled:bg-brand-blue-light/50 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            {isLoading ? 'Refreshing...' : 'Refresh Data'}
          </button>
        </div>
      </div>

      {/* Severity Filter */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Vulnerability Filter</h3>
        <div className="flex flex-wrap gap-4">
          {['all', 'critical', 'high', 'medium', 'low'].map((severity) => (
            <button
              key={severity}
              onClick={() => setSelectedSeverity(severity)}
              className={`px-4 py-2 rounded-xl font-bold transition-colors ${
                selectedSeverity === severity
                  ? 'bg-brand-blue text-white'
                  : 'bg-frost-white text-gray-600 hover:text-deep-navy hover:bg-ice-teal-light'
              }`}
            >
              {severity.charAt(0).toUpperCase() + severity.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Vulnerabilities List */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Vulnerabilities</h3>
        {filteredVulnerabilities.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-500 text-lg">
              {selectedSeverity === 'all' ? 'No vulnerabilities found' : `No ${selectedSeverity} vulnerabilities`}
            </div>
            <div className="text-gray-400 text-sm mt-2">
              {selectedSeverity === 'all' ? 'Run a security scan to check for vulnerabilities' : 'All clear for this severity level'}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredVulnerabilities.map((vuln) => (
              <div key={vuln.id} className="bg-frost-white rounded-xl p-4 border border-ice-teal-light">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-bold text-deep-navy">{vuln.title}</h4>
                  <div className="flex gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${getSeverityColor(vuln.severity)}`}>
                      {vuln.severity}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${getStatusColor(vuln.status)}`}>
                      {vuln.status}
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-2">{vuln.description}</p>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Package:</span>
                    <span className="text-gray-800 ml-2 font-mono">{vuln.package}@{vuln.version}</span>
                  </div>
                  {vuln.fixedVersion && (
                    <div>
                      <span className="text-gray-500">Fixed in:</span>
                      <span className="text-scarf-green ml-2 font-mono">{vuln.fixedVersion}</span>
                    </div>
                  )}
                  {vuln.cve && (
                    <div>
                      <span className="text-gray-500">CVE:</span>
                      <span className="text-brand-blue-light ml-2 font-mono">{vuln.cve}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Category:</span>
                    <span className="text-gray-800 ml-2">{vuln.category}</span>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-400">
                  Discovered: {new Date(vuln.discovered).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityComponent;
